import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Dialog, DialogContent, IconButton, Tooltip, Typography } from "@mui/material";
import { useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useDateFilter } from "../../hooks/useFilterContext";
import { AuditDetailView, AuditListView } from "./components";
import { useAuditDashboardStoreAuditSummaries, useAuditDashboardStoreBackstockLedger } from "./hooks";
import { buildAuditListCards } from "./utils";
import { useAuditsCompleteThroughDate } from "features/audits/hooks/queries/useAuditsCompleteThroughDate";
import StoreWelcomePanel from "features/store/components/StoreWelcomePanel";
import BackstockAuditDueBadge from "./components/shared/BackstockAuditDueBadge";
import type { StoreRouteContext } from "features/store/types";
import {
  hasCompletedStoreAudit,
  isStoreInTrialPeriod,
  isStoreWelcomePreviewEnabled,
  shouldHighlightWelcomeGuide,
} from "features/store/utils/storeWelcomeState";

const SELECTED_REPORT_DATE_QUERY_KEY = "report_date";
const welcomeDismissedStorageKey = (storeId: string) => `lottoreco:welcome-guide-dismissed:${storeId}`;

export default function AuditListDetailPage() {
  const { id: storeId } = useParams<{ id: string }>();
  const { storeDetails, isStoreLoading = false } = useOutletContext<StoreRouteContext>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { startDate, endDate } = useDateFilter();
  const [selectedReportDate, setSelectedReportDate] = useState<string | null>(() =>
    searchParams.get(SELECTED_REPORT_DATE_QUERY_KEY)
  );
  const [orderedReportDates, setOrderedReportDates] = useState<string[]>([]);
  const [welcomeDismissed, setWelcomeDismissed] = useState(() =>
    Boolean(storeId && localStorage.getItem(welcomeDismissedStorageKey(storeId)) === "1")
  );
  const [welcomeOpenRequested, setWelcomeOpenRequested] = useState(false);

  useEffect(() => {
    setWelcomeDismissed(
      Boolean(storeId && localStorage.getItem(welcomeDismissedStorageKey(storeId)) === "1")
    );
    setWelcomeOpenRequested(false);
  }, [storeId]);

  const auditSummaryQuery = useAuditDashboardStoreAuditSummaries(
    storeId && startDate && endDate ? { storeId, startDate, endDate } : null
  );
  const backstockLedgerQuery = useAuditDashboardStoreBackstockLedger(
    storeId && startDate && endDate ? { storeId, startDate, endDate } : null
  );
  const auditsCompleteThroughDateQuery = useAuditsCompleteThroughDate(storeId);
  const effectiveDate = auditsCompleteThroughDateQuery.data?.data?.effective_date as string | undefined;

  const auditListCards = useMemo(() => {
    if (!auditSummaryQuery.data) {
      return [];
    }

    return buildAuditListCards(auditSummaryQuery.data, backstockLedgerQuery.data ?? null, effectiveDate);
  }, [auditSummaryQuery.data, backstockLedgerQuery.data, effectiveDate]);

  useEffect(() => {
    setOrderedReportDates(auditListCards.filter((card) => card.variant === "date").map((card) => card.id));
  }, [auditListCards]);

  useEffect(() => {
    const nextSelectedReportDate = searchParams.get(SELECTED_REPORT_DATE_QUERY_KEY);
    setSelectedReportDate((currentSelectedReportDate) =>
      currentSelectedReportDate === nextSelectedReportDate ? currentSelectedReportDate : nextSelectedReportDate
    );
  }, [searchParams]);

  const handleSelectReportDate = useCallback(
    (reportDate: string) => {
      setSelectedReportDate(reportDate);
      setSearchParams(
        (currentParams) => {
          const nextParams = new URLSearchParams(currentParams);
          nextParams.set(SELECTED_REPORT_DATE_QUERY_KEY, reportDate);
          return nextParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const goToPrevDate = useCallback(() => {
    if (!selectedReportDate || !orderedReportDates.length) return;
    const currentIndex = orderedReportDates.indexOf(selectedReportDate);
    if (currentIndex > 0) {
      handleSelectReportDate(orderedReportDates[currentIndex - 1]);
    }
  }, [handleSelectReportDate, orderedReportDates, selectedReportDate]);

  const goToNextDate = useCallback(() => {
    if (!selectedReportDate || !orderedReportDates.length) return;
    const currentIndex = orderedReportDates.indexOf(selectedReportDate);
    if (currentIndex >= 0 && currentIndex < orderedReportDates.length - 1) {
      handleSelectReportDate(orderedReportDates[currentIndex + 1]);
    }
  }, [handleSelectReportDate, orderedReportDates, selectedReportDate]);

  const canGoPrev = selectedReportDate ? orderedReportDates.indexOf(selectedReportDate) > 0 : false;
  const canGoNext =
    selectedReportDate ? orderedReportDates.indexOf(selectedReportDate) >= 0 && orderedReportDates.indexOf(selectedReportDate) < orderedReportDates.length - 1 : false;

  if (isStoreLoading || !storeDetails) {
    return (
      <Box sx={{ display: "grid", minHeight: 260, placeItems: "center" }}>
        <Typography sx={{ color: "#64748B", fontSize: 14 }}>Loading store...</Typography>
      </Box>
    );
  }

  const welcomePreviewEnabled = isStoreWelcomePreviewEnabled(
    searchParams.get("welcome_preview"),
    process.env.NODE_ENV
  );
  const handleDismissWelcome = () => {
    if (welcomeOpenRequested) {
      setWelcomeOpenRequested(false);
      return;
    }

    if (welcomePreviewEnabled) {
      setWelcomeDismissed(true);
      setSearchParams(
        (currentParams) => {
          const nextParams = new URLSearchParams(currentParams);
          nextParams.delete("welcome_preview");
          return nextParams;
        },
        { replace: true }
      );
      return;
    }

    if (storeId) {
      localStorage.setItem(welcomeDismissedStorageKey(storeId), "1");
    }
    setWelcomeDismissed(true);
  };
  const handleOpenWelcome = () => {
    setWelcomeOpenRequested(true);
  };
  const hasCompletedAudit = !welcomePreviewEnabled && hasCompletedStoreAudit(storeDetails.last_audit_date);
  const showFullWelcome =
    welcomeOpenRequested || (!hasCompletedAudit && (welcomePreviewEnabled || !welcomeDismissed));
  const showWelcomeGuideLink =
    process.env.NODE_ENV === "development" ||
    isStoreInTrialPeriod(storeDetails.subscription_status) ||
    shouldHighlightWelcomeGuide(storeDetails.created_at);

  return (
    <Box>
      <Dialog
        aria-labelledby="getting-started-guide-title"
        fullWidth
        maxWidth="xl"
        onClose={handleDismissWelcome}
        open={showFullWelcome}
        scroll="paper"
      >
        <DialogContent sx={{ bgcolor: "#F6F8FB", p: { xs: 1, sm: 2 } }}>
          <StoreWelcomePanel store={storeDetails} onDismiss={handleDismissWelcome} />
        </DialogContent>
      </Dialog>
      <Box sx={{ alignItems: "center", display: "flex", justifyContent: "space-between", mb: "10px" }}>
        <Typography component="h1" variant="h5" sx={{ fontWeight: 600 }}>
          Audit Report
        </Typography>
        <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
          {showWelcomeGuideLink && (
            <Tooltip title="Getting Started Guide">
              <IconButton
                aria-label="Open Getting Started Guide"
                onClick={handleOpenWelcome}
                size="small"
                sx={{
                  bgcolor: "#F4F9FD",
                  border: "1px solid #8DB9DB",
                  color: "#256EA8",
                  fontSize: 17,
                  fontWeight: 800,
                  height: 32,
                  width: 32,
                  "&:hover": { bgcolor: "#E5F2FB", borderColor: "#4D91C5" },
                }}
              >
                ?
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <BackstockAuditDueBadge storeId={storeId} />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "minmax(440px, 490px) minmax(0, 1fr)",
          },
          gap: "10px",
          alignItems: "start",
          width: "100%",
          minWidth: 0,
        }}
      >
        <AuditListView
          selectedReportDate={selectedReportDate}
          onSelectReportDate={handleSelectReportDate}
          onDateOrderChange={setOrderedReportDates}
        />
        <Box sx={{ minWidth: 0 }}>
          <AuditDetailView
            selectedReportDate={selectedReportDate}
            onPrevDate={goToPrevDate}
            onNextDate={goToNextDate}
            canGoPrev={canGoPrev}
            canGoNext={canGoNext}
          />
        </Box>
      </Box>
    </Box>
  );
}
